import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Rx from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { AppSettingService } from '../app.service';
import { CacheService } from '../utility/cache.service';
import { UserInfoService } from '../user/user-info.service';
import Swal from 'sweetalert2';

//this should be 1:1 with MAP API's .net SaveMapType
interface ISaveMapType {
	CareerVersion: string,
	CertificationVersion: string,
	CTEVersion: string,
	MilitaryVersion: string,
	CollegeVersion: string,
	K12Version: string,
	PreKVersion: string
}

export interface ISaveUserMapOption {
	MapOptionId: string,
	Territories: any
}

export interface ITerritoryIPED {
	CatalogueLabel: string,
	IpedsUnitId: string,
	IsUsed: boolean,
	Name: string,
	NonIpedsInstitutionId: string,
	SelectedCollegeBaseName: string,
	Version: string,
	XLoc: number,
	YLoc: number
}

export interface IMapOptionCollege {
	CollegeId: string,
	Name: string
}

export interface IMapOptionIPEDTrritories {
	CTEVersion: ITerritoryIPED,
	CTEVersions: ITerritoryIPED[],
	CareerVersion: ITerritoryIPED,
	CareerVersions: ITerritoryIPED[]
	CertificationVersion: ITerritoryIPED,
	CertificationVersions: ITerritoryIPED[],
	CollegeVersion: ITerritoryIPED,
	CollegeVersions: ITerritoryIPED[],
	K12Version: ITerritoryIPED,
	K12Versions: ITerritoryIPED[],
	MilitaryVersion: ITerritoryIPED,
	MilitaryVersions: ITerritoryIPED[],
	PreKVersion: ITerritoryIPED,
	PreKVersions: ITerritoryIPED[]
}

export interface IMapOptionIPED {
	MapOptionId: string,
	MapOptionName: string,
	MapShapeId: string,
	PolygonTotal: number,
	IsDefault: boolean,
	Hide: boolean,
	InProcess: boolean,
	Territories: IMapOptionIPEDTrritories,
	CollegeDistinctVersions: IMapOptionCollege[]
}

export interface IMapOptionTerritory {
	TerritoryId: number,
	Name: string, //catalog name
	Version: string
	IsAvailable: boolean,
	IsDefault: boolean,
	IsUsed: boolean
	PolygonPosition: number,
	MapName: string,
	K8Name: string,
	HSName: string,
}

export interface IMapOption {
	MapOptionId: string,
	MapOptionName: string,
	MapShapeId: string,
	UnlockPolygon: boolean,
	PolygonTotal: number,
	IsDefault: boolean,
	IsUsed: boolean,
	Hide: boolean,
	InProcess: boolean,
	CareerVersions: IMapOptionTerritory[],
	CertificationVersions: IMapOptionTerritory[],
	CTEVersions: IMapOptionTerritory[],
	MilitaryVersions: IMapOptionTerritory[],
	CollegeVersions: IMapOptionTerritory[],
	K12Versions: IMapOptionTerritory[],
	PreKVersions: IMapOptionTerritory[]
}

export interface IMapVersion {
	Version: string,
	Name: string,
	XLoc: number,
	YLoc:  number,
	IpedsUnitId: string,
	NonIpedsInstitutionId: string,
	CatalogueLabel: string,
	SelectedCollegeBaseName: string,
	IsUsed: boolean,
	MapName: string,
	HSName: string,
	K8Name: string,
	isDisabled: boolean /* used in UI only to disable checkbox */
}

export interface ITerritories {
	CareerVersion: IMapVersion,
	CertificationVersion: IMapVersion,
	CTEVersion: IMapVersion,
	MilitaryVersion: IMapVersion,
	CollegeVersion: IMapVersion,
	K12Version: IMapVersion,
	PreKVersion: IMapVersion,
	/* available version */
	CareerVersions: IMapVersion[],
	CertificationVersions: IMapVersion[],
	CTEVersions: IMapVersion[],
	MilitaryVersions: IMapVersion[],
	CollegeVersions: IMapVersion[],
	K12Versions: IMapVersion[],
	PreKVersions: IMapVersion[],
	/* zone default version */
	ZoneCareer: string,
	ZoneCertification: string,
	ZoneCTE: string,
	ZoneMilitary: string,
	ZoneCollege: string,
	ZoneK12: string,
	ZonePreK: string
}

export interface IAvailableTerritories {
	PreK: IMapVersion[],
	K12: IMapVersion[],
	College: IMapVersion[],
	Military: IMapVersion[],
	CTE: IMapVersion[],
	Certification: IMapVersion[],
	Career: IMapVersion[]
}


@Injectable({
	providedIn: 'root'
})
export class MapAPIService {
	constructor(
		private cacheSrv: CacheService,
		private http: HttpClient,
		private userInfo: UserInfoService
	) { }

	//used for alternative map demo
	alternativeMapDemoId: any = "isAlternativeMapDemoId";

	//private url = AppSettingService.appConfig.informAPIUrl + "MAP";
	private url = AppSettingService.appConfig.JourneyAPIUrl + "Map"; //API2

	private getBlankSaveMapType(): ISaveMapType {
		return {
			CareerVersion: null,
			CertificationVersion: null,
			CTEVersion: null,
			MilitaryVersion: null,
			CollegeVersion: null,
			K12Version: null,
			PreKVersion: null
		}
	}

	commaDelimitedUserMapVersions: string = "";

	getFilledInSaveMapType(userMapResponseObject: any): ISaveMapType {
		let saveMapTypeObj: any = this.getBlankSaveMapType();
		let saveMapTypeKeys: any = Object.keys(saveMapTypeObj);

		for (var i: any = 0; i < saveMapTypeKeys.length; i++) {
			//this works because userMapResponse object has same keys as saveMapType
			//so we "fill in" the value for each saveMapType key with the corresponding userMapResponse object key's value
			saveMapTypeObj[saveMapTypeKeys[i]] = userMapResponseObject[saveMapTypeKeys[i]]['Version'];
		}

		return saveMapTypeObj;
	}

	getUserMapSavePostData(getUserMapResponse: any): ISaveUserMapOption {
		let postData: any = {
			MapOptionId: null,
			Territories: {
			}
		};
		postData.MapOptionId = getUserMapResponse.MapOptionId;
		if (getUserMapResponse.PreKVersion) { postData.Territories.PreKVersion = getUserMapResponse.PreKVersion.Version; }
		if (getUserMapResponse.K12Version) { postData.Territories.K12Version = getUserMapResponse.K12Version.Version; }
		if (getUserMapResponse.CollegeVersion) { postData.Territories.CollegeVersion = getUserMapResponse.CollegeVersion.Version; }
		if (getUserMapResponse.CareerVersion) { postData.Territories.CareerVersion = getUserMapResponse.CareerVersion.Version; }
		if (getUserMapResponse.MilitaryVersion) { postData.Territories.MilitaryVersion = getUserMapResponse.MilitaryVersion.Version; }
		if (getUserMapResponse.CTEVersion) { postData.Territories.CTEVersion = getUserMapResponse.CTEVersion.Version; }
		if (getUserMapResponse.CertificationVersion) { postData.Territories.CertificationVersion = getUserMapResponse.CertificationVersion.Version; }
		return postData;
	}

	viewMap(minX: number, maxX: number, minY: number, maxY: number, zoom: number, versions: string, selectedObjectId: string, mapOptionId: string = 'default', clipMinX: number = null, clipMinY: number = null, clipMaxX: number = null, clipMaxY: number = null): Rx.Observable<any> {
		if (clipMinX == null || clipMinY == null || clipMaxX == null || clipMaxY == null) {
			clipMinX = -1;
			clipMinY = -1;
			clipMaxX = -1;
			clipMaxY = -1;
		}

		return this.http.get<any>(
			this.url + "/Option/View/" + mapOptionId + "?minX=" + minX.toFixed(4) + "&maxX=" + maxX.toFixed(4) + "&minY=" + minY.toFixed(4) + "&maxY=" + maxY.toFixed(4) + "&zoom=" + zoom.toFixed(4) + "&mvs=" + encodeURIComponent(versions) + "&so=" + encodeURIComponent(selectedObjectId) + "&clipMinX=" + encodeURIComponent(clipMinX) + "&clipMinY=" + encodeURIComponent(clipMinY) + "&clipMaxX=" + encodeURIComponent(clipMaxX) + "&clipMaxY=" + encodeURIComponent(clipMaxY),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getMapObject(objectId: string, mapOptionId: string = 'default'): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Object/" + encodeURIComponent(objectId) + "?mapOptionId=" + mapOptionId,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getMapObjectAncestory(objectId: string): Rx.Observable<any> {
		let l = this.url + "/Ancestory/" + encodeURIComponent(objectId);
		return this.cacheSrv.get(true, l);
	}

	getSolrMapSearch(mapOptionId: string = 'default', keyword: string, limit: number, offset: number, searchOnlyMapVersion: string, myCareerfilterObject: any): Rx.Observable<any> {
		const myCareerfilterObjectStringified = Object.keys(myCareerfilterObject).map((key: any) => key + '=' + myCareerfilterObject[key + '']).join('&');
		return this.http.get<any>(
			this.url + '/MapSearch/' + mapOptionId + '?keyword=' + encodeURIComponent(keyword) + '&limit=' + limit + '&offset=' + offset + '&searchOnlyTerritory=' + searchOnlyMapVersion + '&' + myCareerfilterObjectStringified,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getMapObjectStatus(id: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Search/Status/" + id,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getClientMap(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Client",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getDemoPermission(userGuid: string): Rx.Observable<boolean> {
		return this.http.get<any>(
			this.url + "/DemoPermission/" + encodeURIComponent(userGuid),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		)
	}


	private userMapCache: any = null;

	clearMapCache(): void {
		this.userMapCache = null;
	}

	getUserMap(reload: boolean = false, mapOptionId: string = 'default'): Rx.Observable<any> {
		reload = true; //need to make cache work with get all map versions (when mapOptionId is null)
		if (!reload && this.userMapCache != null) {
			return Rx.of(this.userMapCache);
		}
		else {
			//null value for _default returns available map options: 
			//'default' returns default map for user, 
			//other values return maps for given value.
			let restOfUrl = "/Option/User/" + mapOptionId;
			if (mapOptionId === null) {
				restOfUrl = "/Option/User";
			}

			return this.http.get<any>(
				this.url + restOfUrl,
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			).pipe(
				tap(
					response => {
						this.userMapCache = response;
					}
				),
				
				map(response => this.getUserMapSelectionsIntersectedWithAvailable(response))
			);
		}
	}

	//for clarity, providing these
	getUserMapById(mapOptionId: string = 'default') {
		return this.http.get<any>(
			this.url + "/Option/User/" + encodeURIComponent(mapOptionId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
	getUserMapsAll(): Rx.Observable<any> {	
		return this.http.get<any>(
			this.url + "/Option/User",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	convertToList(getUserMapResponse: any): string {
		let vls: string[] = [];
		Object.keys(getUserMapResponse).forEach((key) => {
			// collect the data in Version not in Versions
			if (key.split("").reverse().join("").indexOf("s") > 0) {
				if (getUserMapResponse[key]) {
					vls.push(getUserMapResponse[key]["Version"]);
				}
			}
		});
		// sort and join the version to a list by territory id , such 1-1,2,1,3-1...
		let retStr = vls.sort().join(",");

		//remove trailing comma if exist
		if (retStr.length > 0 && retStr[retStr.length - 1] === ',') {
			retStr = retStr.slice(0, retStr.length - 1)
		}

		return retStr;
	};

	getUserMapVersionList(reload: boolean = false, mapOptionId: string = 'default'): Rx.Observable<any> { // reutn selected map versions in comma list
		reload = reload == null ? false : reload;
		if (reload) {
			this.clearMapCache();
		}

		if (!reload && this.userMapCache != null) {
			return Rx.of(this.convertToList(this.userMapCache));
		}
		else {
			return this.http.get<any>(
				this.url + '/Option/User/' + mapOptionId,
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			).pipe(			
				map(
					response => {
						response = this.getUserMapSelectionsIntersectedWithAvailable(response);
						this.userMapCache = response;
						return this.convertToList(response);
					}
				)
			);
		}
	}

	saveUserMap(mapSelection: ISaveUserMapOption): Rx.Observable<any> {
		return this.http.post<any>(
			this.url + "/Option/User",
			mapSelection,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		).pipe(
			tap(
				response => {
					this.clearMapCache();
				}
			)
		);
	}

	getCorrespondingMapObject(fromOccupationId: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/GetCorrespondingMapObject/" + fromOccupationId,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getUserCollegesByCip(cip: string, mappObjectId: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/GetUserCollegesByCip/" + encodeURIComponent(cip) + "/" + encodeURIComponent(mappObjectId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	GetUserCollegesByOccupation(mapObjectId: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/GetUserCollegesByOccupation/" + encodeURIComponent(mapObjectId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	setCommaDelimitedUserMapVersions() {
		this.getUserMapVersionList().subscribe(
			(commaDelimitedUserMapVersions) => {
				this.commaDelimitedUserMapVersions = commaDelimitedUserMapVersions;
			});
	}

	//#region map option
	getUserMapOptions(limit: number, offset: number): Rx.Observable<IMapOptionIPED[]> {
		return this.http.get<IMapOptionIPED[]>(
			this.url + "/Option/User?limit=" + limit.toString() + "&offset=" + offset.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	// when mapOptionId is not provided, get the default
	getUserMapOption(reload: boolean = false, mapOptionId: string = null): Rx.Observable<IMapOptionIPEDTrritories> {
		reload = reload == null ? false : reload;
		if (reload) {
			this.clearMapCache();
		}

		if (!reload && this.userMapCache != null) {
			return Rx.of(this.userMapCache);
		}
		else {
			return this.http.get<IMapOptionIPED>(
				this.url + "/Option/User/" + encodeURIComponent(mapOptionId == null ? "default" : mapOptionId),
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			).pipe(
				map(
					response => {
						const _response = this.getUserMapSelectionsIntersectedWithAvailable(response);
						this.userMapCache = (_response as any).Territories;
						return (_response as any).Territories;
					}
				)
			);
		}
	}

	getZoneMapOptions(limit: number, offset: number): Rx.Observable<IMapOption[]> {
		return this.http.get<IMapOption[]>(
			this.url + "/Option/Zone?limit=" + limit.toString() + "&offset=" + offset.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getZoneMapOption(mapOptionId: string, forNew: boolean, mapShapeId: number): Rx.Observable<IMapOption> {
		if (forNew) {
			return this.http.get<IMapOption>(
				this.url + "/Option/Zone/New/" + encodeURIComponent(mapShapeId),
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			);
		}
		else {
			return this.http.get<IMapOption>(
				this.url + "/Option/Zone/" + encodeURIComponent(mapOptionId == null ? "default" : mapOptionId),
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			);
		}
	}


	toggleMapOptionHidden(mapOptionId: string, hide: boolean): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Option/Zone/" + encodeURIComponent(mapOptionId) + "/Hide/" + hide.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	toggleMapOptionDefault(mapOptionId: string, isDefault: boolean): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Option/Zone/" + encodeURIComponent(mapOptionId) + "/Default/" + isDefault.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveZoneMapOption(option: IMapOption): Rx.Observable<IMapOption> {
		return this.http.post<any>(
			this.url + "/Option/Zone",
			option,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	deleteZoneMapOption(mapOptionId: string): Rx.Observable<any> {
		return this.http.delete<any>(
			this.url + "/Option/Zone/" + encodeURIComponent(mapOptionId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getVersion(version: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Version/" + encodeURIComponent(version),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	updateVersion(version: string, name: string, hs: string, k8: string): Rx.Observable<any> {
		return this.http.put<any>(
			this.url + "/Version/" + version + "?mapName=" + encodeURIComponent(name) + "&hsName=" + encodeURIComponent(hs) + "&k8Name=" + encodeURIComponent(k8),
			null,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
	//#endregion map option
	getBlankMapTerritoryVersionObj(): {
		CTEVersion: null | string;
		CareerVersion: null | string;
		CertificationVersion: null | string;
		CollegeVersion: null | string;
		K12Version: null | string;
		MilitaryVersion: null | string;
		PreKVersion: null | string;
	} {
		return {
			CTEVersion: null,
			CareerVersion: null,
			CertificationVersion: null,
			CollegeVersion: null,
			K12Version: null,
			MilitaryVersion: null,
			PreKVersion: null
		};
	}

	getMapTerritoryVersionObjFromGetUserMapResponse(getUserMapResponse: any): any {
		let returnObj = this.getBlankMapTerritoryVersionObj();

		returnObj.CTEVersion = (getUserMapResponse.CTEVersion) ? getUserMapResponse.CTEVersion.Version : null;
		returnObj.CareerVersion = (getUserMapResponse.CareerVersion) ? getUserMapResponse.CareerVersion.Version : null;
		returnObj.CertificationVersion = (getUserMapResponse.CertificationVersion) ? getUserMapResponse.CertificationVersion.Version : null;
		returnObj.CollegeVersion = (getUserMapResponse.CollegeVersion) ? getUserMapResponse.CollegeVersion.Version : null;
		returnObj.K12Version = (getUserMapResponse.K12Version) ? getUserMapResponse.K12Version.Version : null;
		returnObj.MilitaryVersion = (getUserMapResponse.MilitaryVersion) ? getUserMapResponse.MilitaryVersion.Version : null;
		returnObj.PreKVersion = (getUserMapResponse.PreKVersion) ? getUserMapResponse.PreKVersion.Version : null;

		return returnObj;
	}

	getMapSavePostDataFromTerritoryVersionsObj(territoryVersionsObj: any, mapOptionId: string): any {
		var postObj: any = {
			MapOptionId: mapOptionId,
			Territories: {
				CTEVersion: null,
				CareerVersion: null,
				CertificationVersion: null,
				CollegeVersion: null,
				K12Version: null,
				MilitaryVersion: null,
				PreKVersion: null
			}
		};

		postObj.Territories.CTEVersion = (territoryVersionsObj.CTEVersion) ? territoryVersionsObj.CTEVersion : null;
		postObj.Territories.CareerVersion = (territoryVersionsObj.CareerVersion) ? territoryVersionsObj.CareerVersion : null;
		postObj.Territories.CertificationVersion = (territoryVersionsObj.CertificationVersion) ? territoryVersionsObj.CertificationVersion : null;
		postObj.Territories.CollegeVersion = (territoryVersionsObj.CollegeVersion) ? territoryVersionsObj.CollegeVersion : null;
		postObj.Territories.K12Version = (territoryVersionsObj.K12Version) ? territoryVersionsObj.K12Version : null;
		postObj.Territories.MilitaryVersion = (territoryVersionsObj.MilitaryVersion) ? territoryVersionsObj.MilitaryVersion : null;
		postObj.Territories.PreKVersion = (territoryVersionsObj.PreKVersion) ? territoryVersionsObj.PreKVersion : null;

		return postObj;
	}

	getMapSavePostDataFromGetUserMapTerritoryVersionsObj(territoryVersionsObj: any, mapOptionId: string): any {
		var postObj: any = {
			MapOptionId: mapOptionId,
			Territories: {
				CTEVersion: null,
				CareerVersion: null,
				CertificationVersion: null,
				CollegeVersion: null,
				K12Version: null,
				MilitaryVersion: null,
				PreKVersion: null
			}
		};

		postObj.Territories.CTEVersion = (territoryVersionsObj.CTEVersion) ? territoryVersionsObj.CTEVersion.Version : null;
		postObj.Territories.CareerVersion = (territoryVersionsObj.CareerVersion) ? territoryVersionsObj.CareerVersion.Version : null;
		postObj.Territories.CertificationVersion = (territoryVersionsObj.CertificationVersion) ? territoryVersionsObj.CertificationVersion.Version : null;
		postObj.Territories.CollegeVersion = (territoryVersionsObj.CollegeVersion) ? territoryVersionsObj.CollegeVersion.Version : null;
		postObj.Territories.K12Version = (territoryVersionsObj.K12Version) ? territoryVersionsObj.K12Version.Version : null;
		postObj.Territories.MilitaryVersion = (territoryVersionsObj.MilitaryVersion) ? territoryVersionsObj.MilitaryVersion.Version : null;
		postObj.Territories.PreKVersion = (territoryVersionsObj.PreKVersion) ? territoryVersionsObj.PreKVersion.Version : null;

		return postObj;
	}


	getMapSavePostDataFromGetUserMapResponse(getUserMapResponse: any): any {
		var postObj: any = {
			MapOptionId: getUserMapResponse.MapOptionId,
			Territories: {
				CTEVersion: null,
				CareerVersion: null,
				CertificationVersion: null,
				CollegeVersion: null,
				K12Version: null,
				MilitaryVersion: null,
				PreKVersion: null
			}
		};

		postObj.Territories.CTEVersion = (getUserMapResponse.CTEVersion) ? getUserMapResponse.CTEVersion.Version : null;
		postObj.Territories.CareerVersion = (getUserMapResponse.CareerVersion) ? getUserMapResponse.CareerVersion.Version : null;
		postObj.Territories.CertificationVersion = (getUserMapResponse.CertificationVersion) ? getUserMapResponse.CertificationVersion.Version : null;
		postObj.Territories.CollegeVersion = (getUserMapResponse.CollegeVersion) ? getUserMapResponse.CollegeVersion.Version : null;
		postObj.Territories.K12Version = (getUserMapResponse.K12Version) ? getUserMapResponse.K12Version.Version : null;
		postObj.Territories.MilitaryVersion = (getUserMapResponse.MilitaryVersion) ? getUserMapResponse.MilitaryVersion.Version : null;
		postObj.Territories.PreKVersion = (getUserMapResponse.PreKVersion) ? getUserMapResponse.PreKVersion.Version : null;

		return postObj;
	}

	showMapRemovedAlert(mapRemovedAlertText) {
		Swal.fire({
			title: 'Selected Map(s) Have Been Removed',
			text: mapRemovedAlertText,
			showCancelButton: false,
			showConfirmButton: true,
			icon: 'warning'
		});
	}

	getMapChangedAlertText(mapOptionUserResponse: any, mapOptionZoneResponse: any): string {
		//set alert text
		let unavailableMaps = [];
		const unavailableSelectedVersions: any = this.getUserSelectedMapsNotInAvailableMaps(mapOptionUserResponse, mapOptionZoneResponse);
		for (const key in unavailableSelectedVersions) {
			if (unavailableSelectedVersions[key].exists && !unavailableSelectedVersions[key].isSelectedAvailable) {
				unavailableMaps.push(unavailableSelectedVersions[key].territory + ' - ' + unavailableSelectedVersions[key].mapName);
			}
		}
		console.log('unavailableMapNames: ', unavailableMaps);
		let alertMapRemovedText = 'The following map(s) you had selected are no longer available: ' + unavailableMaps.join(', ') + '.';

		return alertMapRemovedText;
	}

	checkUserMapIncludesAllZoneMapVersions(mapOptionUserResponse: any): boolean {
		return (
			//this works because xVersions will always include all available maps from zone while xVersion will always be user selected version when user has selected a version.
			(mapOptionUserResponse.ZoneCTE ? (mapOptionUserResponse.CTEVersions.some(o => o.Version == mapOptionUserResponse.CTEVersion.Version)) : true)
			&&
			(mapOptionUserResponse.ZoneCertification ? (mapOptionUserResponse.CertificationVersions.some(o => o.Version == mapOptionUserResponse.CertificationVersion.Version)) : true)
			&&
			(mapOptionUserResponse.ZoneCareer ? (mapOptionUserResponse.CareerVersions.some(o => o.Version == mapOptionUserResponse.CareerVersion.Version)) : true)
			&&
			(mapOptionUserResponse.ZoneCollege ? (mapOptionUserResponse.CollegeVersions.some(o => o.Version == mapOptionUserResponse.CollegeVersion.Version)) : true)
			&&
			(mapOptionUserResponse.ZoneMilitary ? (mapOptionUserResponse.MilitaryVersions.some(o => o.Version == mapOptionUserResponse.MilitaryVersion.Version)) : true)
			&&
			(mapOptionUserResponse.ZoneK12 ? (mapOptionUserResponse.K12Versions.some(o => o.Version == mapOptionUserResponse.K12Version.Version)) : true)
			&&
			(mapOptionUserResponse.ZonePreK ? (mapOptionUserResponse.PreKVersions.some(o => o.Version == mapOptionUserResponse.PreKVersion.Version)) : true)
		);
	}

	getUserSelectedMapsNotInAvailableMaps(mapOptionUserResponse: any, mapOptionZoneResponse: any): any {
		let noLongerAvailableUserSelectedMaps = {
			CTE: {
				mapName: (mapOptionUserResponse.ZoneCTE ? (mapOptionZoneResponse.CTEVersions.find(o => o.Version == mapOptionUserResponse.CTEVersion.Version).MapName) : ''),
				territory: "CTE",
				exists: mapOptionUserResponse.ZoneCTE ? true : false,
				isSelectedAvailable: (mapOptionUserResponse.ZoneCTE ? (mapOptionUserResponse.CTEVersions.some(o => o.Version == mapOptionUserResponse.CTEVersion.Version)) : true)
			},
			Certification: {
				mapName: (mapOptionUserResponse.ZoneCertification ? (mapOptionZoneResponse.CertificationVersions.find(o => o.Version == mapOptionUserResponse.CertificationVersion.Version).MapName) : ''),
				territory: "Certification",
				exists: mapOptionUserResponse.ZoneCertification ? true : false,
				isSelectedAvailable: (mapOptionUserResponse.ZoneCertification ? (mapOptionUserResponse.CertificationVersions.some(o => o.Version == mapOptionUserResponse.CertificationVersion.Version)) : true),
			},
			Career: {
				mapName: (mapOptionUserResponse.ZoneCareer ? (mapOptionZoneResponse.CareerVersions.find(o => o.Version == mapOptionUserResponse.CareerVersion.Version).MapName) : ''),
				territory: "Career",
				exists: mapOptionUserResponse.ZoneCareer ? true : false,
				isSelectedAvailable: (mapOptionUserResponse.ZoneCareer ? (mapOptionUserResponse.CareerVersions.some(o => o.Version == mapOptionUserResponse.CareerVersion.Version)) : true),
			},
			College: {
				mapName: (mapOptionUserResponse.ZoneCollege ? (mapOptionZoneResponse.CollegeVersions.find(o => o.Version == mapOptionUserResponse.CollegeVersion.Version).MapName) : ''),
				territory: "College",
				exists: mapOptionUserResponse.ZoneCollege ? true : false,
				isSelectedAvailable: (mapOptionUserResponse.ZoneCollege ? (mapOptionUserResponse.CollegeVersions.some(o => o.Version == mapOptionUserResponse.CollegeVersion.Version)) : true),
			},
			Military: {
				mapName: (mapOptionUserResponse.ZoneMilitary ? (mapOptionZoneResponse.MilitaryVersions.find(o => o.Version == mapOptionUserResponse.MilitaryVersion.Version).MapName) : ''),
				territory: "Military",
				exists: mapOptionUserResponse.ZoneMilitary ? true : false,
				isSelectedAvailable: (mapOptionUserResponse.ZoneMilitary ? (mapOptionUserResponse.MilitaryVersions.some(o => o.Version == mapOptionUserResponse.MilitaryVersion.Version)) : true),
			},
			K12: {
				mapName: (mapOptionUserResponse.ZoneK12 ? (mapOptionZoneResponse.K12Versions.find(o => o.Version == mapOptionUserResponse.K12Version.Version).MapName) : ''),
				territory: "K-12",
				exists: mapOptionUserResponse.ZoneK12 ? true : false,
				isSelectedAvailable: (mapOptionUserResponse.ZoneK12 ? (mapOptionUserResponse.K12Versions.some(o => o.Version == mapOptionUserResponse.K12Version.Version)) : true),
			},
			PreK: {
				mapName: (mapOptionUserResponse.ZonePreK ? (mapOptionZoneResponse.PreKVersions.find(o => o.Version == mapOptionUserResponse.PreKVersion.Version).MapName) : ''),
				territory: "Pre-K",
				exists: mapOptionUserResponse.ZonePreK ? true : false,
				isSelectedAvailable: (mapOptionUserResponse.ZonePreK ? (mapOptionUserResponse.PreKVersions.some(o => o.Version == mapOptionUserResponse.PreKVersion.Version)) : true)
			}
		}
		return noLongerAvailableUserSelectedMaps;
	}

	//any time Map/Option/User is called, we need to process response with this function
	getUserMapSelectionsIntersectedWithAvailable(mapOptionUserResponse: any): Rx.Observable<any> {
		//re-map response so if any singleton (currently selected) Version
		//is missing from corresponding array(available) Version,
		//assign first(index 0) array(available) Version
		//to singleton Version

		//todo: make this dyanmic
		if (mapOptionUserResponse.CTEVersion && !mapOptionUserResponse.CTEVersions.some(o => o.Version === mapOptionUserResponse.CTEVersion.Version)) {
			mapOptionUserResponse.CTEVersion = JSON.parse(JSON.stringify(mapOptionUserResponse.CTEVersions.find(o => o.Version === mapOptionUserResponse.ZoneCTE)));
		}
		if (mapOptionUserResponse.CareerVersion && !mapOptionUserResponse.CareerVersions.some(o => o.Version === mapOptionUserResponse.CareerVersion.Version)) {
			mapOptionUserResponse.CareerVersion = JSON.parse(JSON.stringify(mapOptionUserResponse.CareerVersions.find(o => o.Version === mapOptionUserResponse.ZoneCareer)));
		}
		if (mapOptionUserResponse.CertificationVersion && !mapOptionUserResponse.CertificationVersions.some(o => o.Version === mapOptionUserResponse.CertificationVersion.Version)) {
			mapOptionUserResponse.CertificationVersion = JSON.parse(JSON.stringify(mapOptionUserResponse.CertificationVersions.find(o => o.Version === mapOptionUserResponse.ZoneCertification)));
		}
		if (mapOptionUserResponse.CollegeVersion && !mapOptionUserResponse.CollegeVersions.some(o => o.Version === mapOptionUserResponse.CollegeVersion.Version)) {
			mapOptionUserResponse.CollegeVersion = JSON.parse(JSON.stringify(mapOptionUserResponse.CollegeVersions.find(o => o.Version === mapOptionUserResponse.ZoneCollege)));
		}
		if (mapOptionUserResponse.K12Version && !mapOptionUserResponse.K12Versions.some(o => o.Version === mapOptionUserResponse.K12Version.Version)) {
			mapOptionUserResponse.K12Version = JSON.parse(JSON.stringify(mapOptionUserResponse.K12Versions.find(o => o.Version === mapOptionUserResponse.ZoneK12)));
		}
		if (mapOptionUserResponse.MilitaryVersion && !mapOptionUserResponse.MilitaryVersions.some(o => o.Version === mapOptionUserResponse.MilitaryVersion.Version)) {
			mapOptionUserResponse.MilitaryVersion = JSON.parse(JSON.stringify(mapOptionUserResponse.MilitaryVersions.find(o => o.Version === mapOptionUserResponse.ZoneMilitary)));
		}
		if (mapOptionUserResponse.PreKVersion && !mapOptionUserResponse.PreKVersions.some(o => o.Version === mapOptionUserResponse.PreKVersion.Version)) {
			mapOptionUserResponse.PreKVersion = JSON.parse(JSON.stringify(mapOptionUserResponse.PreKVersions.find(o => o.Version === mapOptionUserResponse.ZonePreK)));
		}

		return mapOptionUserResponse;
	}

	getMapVersions(territoryId: number): Rx.Observable<IMapVersion[]> {
		return this.http.get<IMapVersion[]>(
			this.url + "/Territory/" + encodeURIComponent(territoryId) + "/Version",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
}
